import {
  DOCUMENT_VIEWER_TABS_TEST_ID,
  DOCUMENT_VIEWER_TAB_MENU_BUTTON_TEST_ID,
  DOCUMENT_VIEWER_TAB_MENU_ITEM_TEST_ID,
  DOCUMENT_VIEWER_TAB_MENU_TEST_ID,
  DOCUMENT_VIEWER_TAB_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faCheck, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';

export type DocumentViewerTabId = 'document' | 'details' | 'xml' | 'pdf_preview'; // Future: | 'clinical-data' | 'related'
export interface DocumentViewerTab {
  id: DocumentViewerTabId;
  label: string;
  onlyVisibleForXmlDocuments: boolean;
}

export interface DocumentViewerTabsProps {
  tabs: Array<DocumentViewerTab>;
  selectedTab: DocumentViewerTab;
  onTabSelected: (id: DocumentViewerTabId) => void;
}

export const DocumentViewerTabs = ({
  tabs,
  selectedTab,
  onTabSelected,
}: DocumentViewerTabsProps) => {
  return (
    <nav
      data-testid={DOCUMENT_VIEWER_TABS_TEST_ID}
      aria-label="Document Content"
      className={tw`flex gap-1`}
    >
      {tabs.map((tab) => (
        <button
          data-testid={DOCUMENT_VIEWER_TAB_TEST_ID}
          type="button"
          key={tab.id}
          onClick={() => onTabSelected(tab.id)}
          className={twx(
            'block rounded-t-md border border-transparent border-b-0 px-6 py-2 font-bold leading-tight',
            {
              'border-white bg-white text-content-main': selectedTab.id === tab.id,
              'bg-transparent text-white hover:border-white/50 hover:bg-white/25':
                selectedTab.id !== tab.id,
            },
          )}
        >
          <span className={tw`overflow-hidden whitespace-nowrap`}>{tab.label}</span>
        </button>
      ))}
    </nav>
  );
};
export const DocumentViewerTabMenu = ({
  tabs,
  selectedTab,
  onTabSelected,
}: DocumentViewerTabsProps) => {
  return (
    <Listbox value={selectedTab} onChange={(tab) => onTabSelected(tab.id)}>
      <div
        className={tw`relative w-70 min-w-max cursor-default gap-1 rounded-md border border-border-main bg-white text-left shadow-sm sm:text-sm`}
      >
        <ListboxButton
          id="document-tabs"
          data-testid={DOCUMENT_VIEWER_TAB_MENU_BUTTON_TEST_ID}
          className={tw`flex w-full items-center justify-between rounded-md border-none px-4 py-2 font-bold text-content-main text-sm leading-tight focus:outline-4 focus:outline-primary-main focus:outline-offset-[3px] focus-visible:outline-4 focus-visible:outline-primary-main focus-visible:outline-offset-[3px]`}
        >
          <span className={tw`overflow-hidden whitespace-nowrap`}>{selectedTab.label}</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={tw`h-4 w-4 text-content-main`}
            aria-hidden="true"
          />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom"
          data-testid={DOCUMENT_VIEWER_TAB_MENU_TEST_ID}
          className={tw`max-h-60 w-70 min-w-max overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
        >
          {tabs.map((tab) => (
            <ListboxOption
              key={tab.id}
              data-testid={DOCUMENT_VIEWER_TAB_MENU_ITEM_TEST_ID}
              className={tw`group/option relative cursor-pointer select-none py-2 pr-4 pl-10 text-content-main`}
              value={tab}
            >
              <div className={tw`flex items-center justify-start gap-3`}>
                <span
                  className={twx({
                    'overflow-hidden whitespace-nowrap font-semibold text-primary-main':
                      tab.id === selectedTab.id,
                  })}
                >
                  {tab.label}
                </span>
                {tab.id === selectedTab.id ? (
                  <span className={tw`absolute inset-y-0 left-3 flex items-center`}>
                    <FontAwesomeIcon
                      icon={faCheck}
                      className={twx('h-4 w-4', {
                        'text-primary-main': tab.id === selectedTab.id,
                      })}
                      aria-hidden="true"
                    />
                  </span>
                ) : null}
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
